import { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchStore } from '@/store/searchStore';
import Image from 'next/image';

interface FloatingImagesProps {
  className?: string;
  height: string;
  width: string;
  src: string;
  transitionDelay?: number;
}

const getNextImageNumber = (
  currentNumber: number,
  totalImages: number
): number => {
  return currentNumber < totalImages ? currentNumber + 1 : 1;
};

const FloatingImages = ({
  className,
  height,
  width,
  src,
}: FloatingImagesProps) => {
  const imageRef = useRef<HTMLDivElement>(null);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [previousSrc, setPreviousSrc] = useState(src);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const totalImages = 14;
  const isSearchActive = useSearchStore(state => state.isSearchActive);

  // Memoized animation duration to prevent hydration mismatch
  const floatDuration = useMemo(() => 3 + Math.random() * 3, []);
  const floatDelay = useMemo(() => Math.random() * 2, []);

  useEffect(() => {
    if (!imageRef.current || isSearchActive) return;

    imageRef.current.style.animation = `float ${floatDuration}s ease-in-out ${floatDelay}s infinite`;

    const rotateInterval = setInterval(() => {
      setIsTransitioning(true);
      setPreviousSrc(currentSrc);

      setTimeout(() => {
        setCurrentSrc(prevSrc => {
          const currentNumber = parseInt(
            prevSrc.match(/home-(\d+)\.png/)?.[1] || '1'
          );
          return `/assets/images/home/home-${getNextImageNumber(currentNumber, totalImages)}.png`;
        });

        setTimeout(() => setIsTransitioning(false), 1000);
      }, 100);
    }, 10000);

    return () => clearInterval(rotateInterval);
  }, [currentSrc, isSearchActive, totalImages, floatDuration, floatDelay]);

  return (
    <div
      ref={imageRef}
      className={`absolute ${className} pointer-events-none transition-transform transition-opacity duration-300
        ${isSearchActive ? 'opacity-30 translate-x-1/2 translate-y-1/2 scale-50' : ''}`}
      style={{ transition: 'transform 3s ease, opacity 3s ease' }}
    >
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) scale(1); }
            25%, 75% { transform: translateY(-5px) scale(1.005); }
            50% { transform: translateY(-10px) scale(1.01); }
          }
          @media (max-width: 676px) {
            @keyframes float {
              0%, 100% { transform: translateY(0px) scale(1); }
              25%, 75% { transform: translateY(-2px) scale(1.002); }
              50% { transform: translateY(-4px) scale(1.004); }
            }
          }
        `}
      </style>
      <div className='relative float-animation'>
        {/* Previous Image */}
        <Image
          src={previousSrc}
          width={1000}
          height={1000}
          priority
          alt='Previous image'
          className={`${height} ${width} z-0 rounded-lg object-cover transition-opacity duration-1000`}
          style={{ opacity: isTransitioning ? 1 : 1 }}
        />
        {/* Current Image */}
        <Image
          src={currentSrc}
          width={1000}
          height={1000}
          alt='Current image'
          className={`absolute top-0 left-0 ${height} ${width} z-10 rounded-lg object-cover transition-opacity duration-1000
            ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
            ${isSearchActive ? 'opacity-0' : ''}`}
        />
      </div>
    </div>
  );
};

export default FloatingImages;
